// Refresh Monitor — engineering-facing page to spot DB
// problems with ClickHouse refreshes: per workspace × market, shows the latest
// refresh, the span of refreshes present, and a server-computed status. Status
// (ok / stale / gap / one_time) is derived by the API so the client just renders
// it; a GAP (a month missing between two present months) usually means a refresh
// was deleted, and STALE means the latest refresh is behind the expected month.
// One-time workspaces (auto-refresh off) are never flagged stale or gapped.
// Not a delivery-team alert — delivery QA is owned outside engineering.
// Data: GET /admin/delivery-status (dashone apps/api).
import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  Input,
  Button,
  Tag,
  Tooltip,
  Switch,
  Space,
  Spin,
  Alert,
  ConfigProvider,
} from "antd";
import {
  ReloadOutlined,
  SearchOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { fetchDeliveryStatus } from "../api/deliveryApi";
import { humanizeWorkspaceKey } from "../utils/workspace";
import { foldSearch } from "../utils/searchFold";
import {
  STATUS_META,
  NON_ISSUE_STATUSES,
  STATUS_LEGEND,
} from "./refreshMonitorStatus";

const fmt = (r) => (r && r.length === 6 ? r : "—");

// Column header with an info tooltip, used to surface the lookback window.
const HeaderWithInfo = ({ children, tip }) => (
  <span>
    {children}{" "}
    <Tooltip title={tip}>
      <InfoCircleOutlined className="text-[#00000040]" />
    </Tooltip>
  </span>
);

// The API only considers refreshes within this window when deciding Missing /
// Gap / Stale. Surfaced in the UI so the scope of the check is explicit; keep in
// sync with GAP_LOOKBACK_MONTHS in apps/api/src/routes/admin.py.
const GAP_LOOKBACK_MONTHS = 12;
const WINDOW_NOTE = `Only the last ${GAP_LOOKBACK_MONTHS} months of refresh history are used to determine missing refreshes and status.`;

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[13px] text-[#00000073]">{label}</div>
    <div className="text-[20px] font-semibold font-[Satoshi] text-[#000000d9]">
      {value}
    </div>
  </div>
);

const RefreshMonitorPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [onlyIssues, setOnlyIssues] = useState(false);

  const load = useCallback(({ forceRefresh = false } = {}) => {
    setLoading(true);
    setError(null);
    fetchDeliveryStatus({ forceRefresh })
      .then((resp) => setData(resp))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => load(), [load]);

  const globalMax = data?.global_max_refresh || "";
  const expected = data?.expected_refresh || "";

  const rows = useMemo(() => {
    const items = data?.items || [];
    return items.map((it, i) => ({
      key: `${it.id_client}__${it.country}__${i}`,
      ...it,
      _name: humanizeWorkspaceKey(it.id_client),
    }));
  }, [data]);

  const filtered = useMemo(() => {
    const q = foldSearch(search);
    return rows.filter((r) => {
      if (onlyIssues && NON_ISSUE_STATUSES.has(r.status)) return false;
      if (
        q &&
        !foldSearch(`${r.id_client} ${r._name} ${r.country}`).includes(q)
      )
        return false;
      return true;
    });
  }, [rows, search, onlyIssues]);

  const issueCount = useMemo(
    () => rows.filter((r) => !NON_ISSUE_STATUSES.has(r.status)).length,
    [rows],
  );

  if (loading) {
    return (
      <div
        className="ml-[240px]"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <Spin size="large" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="w-[calc(100%-240px)] ml-[240px]" style={{ padding: 24 }}>
        <Alert
          type="error"
          showIcon
          message="Delivery status unavailable"
          description={
            <>
              <div>{error}</div>
              <div style={{ marginTop: 8, color: "#00000073" }}>
                Ensure the dashone API <code>GET /admin/delivery-status</code> is
                deployed.
              </div>
            </>
          }
        />
      </div>
    );
  }

  const columns = [
    {
      title: "Workspace",
      dataIndex: "_name",
      width: 240,
      fixed: "left",
      sorter: (a, b) => a.id_client.localeCompare(b.id_client),
      render: (name, record) => (
        <div>
          <div className="font-medium text-[#000000d9]">{name}</div>
          <div className="text-[12px] text-[#00000073]">{record.id_client}</div>
        </div>
      ),
    },
    {
      title: "Country",
      dataIndex: "country",
      width: 100,
      sorter: (a, b) => a.country.localeCompare(b.country),
    },
    {
      title: "Latest refresh",
      dataIndex: "latest_refresh",
      width: 130,
      sorter: (a, b) =>
        (a.latest_refresh || "").localeCompare(b.latest_refresh || ""),
      render: fmt,
    },
    {
      title: "First refresh",
      dataIndex: "first_refresh",
      width: 130,
      render: fmt,
    },
    {
      title: "# refreshes",
      dataIndex: "refresh_count",
      width: 110,
      sorter: (a, b) => a.refresh_count - b.refresh_count,
    },
    {
      title: <HeaderWithInfo tip={WINDOW_NOTE}>Missing (gaps)</HeaderWithInfo>,
      dataIndex: "missing_refreshes",
      width: 260,
      render: (missing) =>
        missing && missing.length ? (
          <span>
            {missing.map((m) => (
              <Tag key={m} color="red">
                {m}
              </Tag>
            ))}
          </span>
        ) : (
          <span className="text-[#bfbfbf]">—</span>
        ),
    },
    {
      title: <HeaderWithInfo tip={WINDOW_NOTE}>Status</HeaderWithInfo>,
      dataIndex: "status",
      width: 130,
      filters: [
        { text: "Gap", value: "gap" },
        { text: "Stale", value: "stale" },
        { text: "One-time", value: "one_time" },
        { text: "Non-monthly", value: "non_monthly" },
        { text: "OK", value: "ok" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => {
        const s = STATUS_META[status] || STATUS_META.ok;
        const tip =
          status === "gap"
            ? `A refresh is missing in the last ${GAP_LOOKBACK_MONTHS} months — likely an accidental deletion`
            : status === "stale"
              ? `Latest refresh is behind the expected month (${fmt(expected)})`
              : status === "one_time"
                ? "One-time delivery (auto-refresh off) — not expected to receive new refreshes"
                : status === "non_monthly"
                  ? "Active but not on a monthly cadence — gaps and staleness are not flagged"
                  : "Up to date, no gaps";
        return (
          <Tooltip title={tip}>
            <Tag color={s.color}>{s.label}</Tag>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div className="w-[calc(100%-240px)] ml-[240px] min-h-[100vh]">
      <div style={{ padding: "2rem" }}>
        <div className="text-[1.875rem] font-bold font-[Satoshi] text-[#000000d9] mb-1">
          Refresh Monitor
        </div>
        <div className="text-[13px] text-[#00000073] mb-6">
          Per workspace × market: latest refresh and gaps in the refresh history.
          <dl className="mt-2 mb-2">
            {STATUS_LEGEND.map(({ status, description }) => (
              <div key={status} className="flex items-baseline gap-2 mb-1 last:mb-0">
                <dt className="shrink-0">
                  <Tag color={STATUS_META[status].color} style={{ marginInlineEnd: 0 }}>
                    {STATUS_META[status].label}
                  </Tag>
                </dt>
                <dd className="m-0">{description}</dd>
              </div>
            ))}
          </dl>
          <span className="text-[#00000059]">{WINDOW_NOTE}</span>
        </div>

        <div className="bg-white p-6 rounded-3xl border border-[rgba(0,0,0,0.05)] shadow-[2px_4px_12px_0px_rgba(0,0,0,0.02)]">
          <div className="flex flex-wrap items-center gap-10 mb-5">
            <Stat label="Expected refresh (monthly)" value={fmt(expected)} />
            <Stat label="Latest refresh across fleet" value={fmt(globalMax)} />
          </div>

          <Space wrap style={{ marginBottom: 16 }}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search workspace / country"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 320 }}
            />
            <span>
              <Switch
                checked={onlyIssues}
                onChange={setOnlyIssues}
                size="small"
              />{" "}
              <span className="text-[13px]">Only issues ({issueCount})</span>
            </span>
            <Tooltip title="Bypass the backend 1h cache and reload">
              <Button
                size="small"
                icon={<ReloadOutlined spin={loading} />}
                onClick={() => load({ forceRefresh: true })}
                disabled={loading}
              >
                Refresh
              </Button>
            </Tooltip>
          </Space>

          <ConfigProvider
            theme={{
              components: {
                Table: {
                  headerBorderRadius: 8,
                  cellPaddingBlock: 6,
                  cellPaddingInline: 8,
                  fontSize: 13,
                },
              },
            }}
          >
            <Table
              size="small"
              columns={columns}
              dataSource={filtered}
              scroll={{ x: 1040 }}
              pagination={{
                pageSize: 50,
                showSizeChanger: true,
                pageSizeOptions: [25, 50, 100],
                showTotal: (t) => `Total ${t}`,
              }}
            />
          </ConfigProvider>
        </div>
      </div>
    </div>
  );
};

export default RefreshMonitorPage;
